import { Navigate, Outlet, Route, Routes } from 'react-router-dom';

import { FeatureFlagProtectedRoute, useAppId } from '@verantos-dev/app-state-base';
import { withAuth } from '@verantos-dev/core-auth';
import { appIdToClaimCheckDefinitionMap, COHORT_VERSION_PATH } from '@verantos-dev/core-constants';
import { EvidencePlatformSidebarAppID } from '@verantos-dev/core-enums';
import {
  AnalysisDetailPage,
  AnalysisVersionResolverPage,
  VariablePreviewProvider,
  type VariablePreviewRenderer,
} from '@verantos-dev/feature-analysis';
import {
  AnalyticalDatasetDetailPage,
  AnalyticalDatasetVersionResolverPage,
} from '@verantos-dev/feature-analytical-datasets';
import { AvDenovoPreviewModal } from '@verantos-dev/feature-analytical-variables';
import {
  AnalyticalVariableEditPage,
  CohortVersionLayout,
  ProjectCohortDetailPage,
} from '@verantos-dev/feature-cohort-builder';
import {
  CohortVersionResolverPage,
  ProjectArtifactsListPage,
  ProjectConceptGroupCreatePage,
  ProjectConceptGroupPreviewPage,
  ProjectConceptGroupViewPage,
  ProjectDatasetPlaceholderPage,
  ProjectPage,
  ProjectVariableDraftPage,
  ProjectVariableGroupPage,
  ProjectVariableViewPage,
} from '@verantos-dev/feature-project-page';
import { ProjectsListPage } from '@verantos-dev/feature-projects-list';

const APP_ID = EvidencePlatformSidebarAppID.projects;

// Popup opened by analysis table section headers that reference an analytical
// variable. feature-analysis cannot import feature-analytical-variables (it
// would close the cycle feature-analysis → feature-analytical-variables →
// feature-epi-chat → feature-analysis), so this route file — which imports
// both — supplies it via VariablePreviewProvider.
const renderAnalysisVariablePreview: VariablePreviewRenderer = ({ avId, onClose }) => (
  <AvDenovoPreviewModal
    id="analysis-av-preview"
    analyticalVariableId={avId ?? ''}
    isOpen={Boolean(avId)}
    onClose={onClose}
  />
);

export const ProjectsUiRoutes = () => {
  useAppId(APP_ID);

  return (
    <Routes>
      <Route index element={<ProjectsListPage />} />
      <Route path=":projectId" element={<ProjectPage />}>
        <Route index element={<ProjectArtifactsListPage />} />

        {/* Multi-chat surface (OR-5963 URL refactor): the active chat
            now rides on the ``?chat=<id>`` query param rather than a
            path segment. This keeps chat selection orthogonal to the
            artifact-scope path routes below — flipping between a
            cohort and a variable preserves the open chat, and
            switching chats preserves the open scope route.
            `ProjectPage` reads ``chat`` from search params and mounts
            the active chat sidecar. No dedicated path route needed. */}

        {/* Agent-first artifact detail routes (phase 4). The cohort routes keep
            cohort-version/:cohortVersionId in the URL for compatibility with
            the existing builder; the breadcrumb hides that segment. */}
        <Route path="cohort/:cohortId">
          <Route index element={<CohortVersionResolverPage />} />
          {/* Cohort-scoped variable view. Surfaces the same content as
              ``/projects/:pid/variable/:avId`` (the project-level view)
              but lets the URL carry the cohort context, so the
              breadcrumb hook renders ``Projects / <project> / <cohort>
              / <variable>`` automatically. Used by the cohort
              criterion modal's "View full" link for av_denovo refs.
              The cohort-version segment is omitted on purpose — read
              surfaces don't care about which working version was open
              when the user clicked through. */}
          <Route path="variable/:analyticalVariableId" element={<ProjectVariableViewPage />} />
          <Route path={COHORT_VERSION_PATH}>
            <Route path=":cohortVersionId" element={<CohortVersionLayout />}>
              <Route index element={<ProjectCohortDetailPage />} />
              <Route
                path="variable/:analyticalVariableId"
                element={<AnalyticalVariableEditPage />}
              />
            </Route>
          </Route>
        </Route>
        <Route path="variable/:analyticalVariableId" element={<ProjectVariableViewPage />} />
        {/* Agent-driven draft surface (OR-5963 step 6). The right pane
            follows the agent here when it emits a ``variable_draft``
            focus_scope event. Cohort + concept-group draft analogues
            land when the orchestrator emits them. */}
        <Route path="draft/variable/:clientId" element={<ProjectVariableDraftPage />} />
        {/* OR-6504: a proposed variable GROUP — several variables sharing one
            definition. Keyed by family id because nothing is created until the
            researcher approves, so there is no asset id yet; the page renders
            from the replayed approval checkpoint. */}
        <Route path="variable-group/:familyId" element={<ProjectVariableGroupPage />} />
        <Route path="dataset/:datasetId" element={<ProjectDatasetPlaceholderPage />} />
        <Route path="concept-group/:conceptGroupId" element={<ProjectConceptGroupViewPage />} />
        {/* OR-6288 W5: the in-page create entry (flag-ON replaces the modal). */}
        <Route path="concept-group-create" element={<ProjectConceptGroupCreatePage />} />
        {/* OR-6126: read-only review surface for a proposed (preview) concept
            group — Create resolves it to the library group in place. */}
        <Route
          path="concept-group-preview/:previewId"
          element={<ProjectConceptGroupPreviewPage />}
        />

        {/* Analytical datasets — detail page only. Entry is the artifacts page
            (row-click + create); the standalone list page is retired so the
            index redirects to the project home. Profile + version id mirror
            cohort; the unversioned form hits the resolver. */}
        <Route path="analytical-datasets">
          <Route index element={<Navigate to=".." replace />} />
          <Route path=":analyticalDatasetProfileId">
            <Route index element={<AnalyticalDatasetVersionResolverPage />} />
            <Route path="analytical-dataset-version/:analyticalDatasetVersionId">
              <Route index element={<AnalyticalDatasetDetailPage />} />
            </Route>
          </Route>
        </Route>

        {/* Analysis — detail page only, same shape as analytical
            datasets: profile + version id, unversioned form hits the
            resolver. No standalone list route — the unified project-artifacts
            list is the only entry surface.
            TODO: cleanup with descriptive_analysis_impl ff — drop the
            FeatureFlagProtectedRoute wrapper and leave the routes bare
            (mirrors analytical-datasets above once that flag was retired). */}
        <Route
          path="analysis"
          element={
            <FeatureFlagProtectedRoute
              featureFlag="descriptive_analysis_impl"
              fallbackPath=".."
              element={<Outlet />}
            />
          }
        >
          <Route path=":analysisProfileId">
            <Route index element={<AnalysisVersionResolverPage />} />
            <Route path="analysis-version/:analysisVersionId">
              <Route
                index
                element={
                  <VariablePreviewProvider renderPreview={renderAnalysisVariablePreview}>
                    <AnalysisDetailPage />
                  </VariablePreviewProvider>
                }
              />
            </Route>
          </Route>
        </Route>
      </Route>
    </Routes>
  );
};

export default withAuth(ProjectsUiRoutes, APP_ID, appIdToClaimCheckDefinitionMap);
